import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { useGetCaseStatus } from 'pages/Cases/hooks/useGetCaseStatus';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Typography,
  CircularProgress,
  MenuItem,
  Menu,
  Grid,
  IconButton,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import moment from 'moment';
import {
  Case,
  CaseMilestone,
  CaseMilestoneAction,
} from 'pages/Cases/hooks/useGetCases';
import { actions as chatActions } from '@features/Chat/slice';
import PrompQuestions from '@components/PrompQuestions';
import { useGetDownloadPerformanceReportPDF } from 'pages/Cases/hooks/useGetDownloadPerformanceReportPDF';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useCreatePortfolioConstructionByConsultForm } from 'pages/Conversations/hooks/useCreatePortfolioConstructionByConsultForm';
import { LoadingButton } from '@mui/lab';
import { useQueryClient } from 'react-query';
import { DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { useDeleteMilestoneActions } from 'pages/Cases/hooks/useDeleteMilestoneActions';
import DeleteMilestoneActionModal from '../DeleteMilestoneActionModal';
import ErrorDeleteMilestoneModal from '../ErrorDeleteMilestone';

type AccordionExpandIconProps = {
  caseData: Case;
  milestone: CaseMilestone;
  index: number;
};

const AccordionExpandIcon: React.FC<AccordionExpandIconProps> = ({
  caseData,
  milestone,
  index,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [portfolioConstructionActions, setPortfolioConstructionActions] =
    React.useState<CaseMilestoneAction[]>([]);
  // Initial determination of loading state
  const queryClient = useQueryClient();
  const { caseStatus } = useGetCaseStatus(caseData.id, true);
  const { deleteMilestoneActionsMutate, error: deleteMilestoneActionsError } =
    useDeleteMilestoneActions();
  const [actionToDelete, setActionToDelete] =
    React.useState<CaseMilestoneAction | null>(null);
  const [isDeleteMilestoneErrorModalOpen, setIsDeleteMilestoneErrorModalOpen] =
    React.useState(false);
  React.useEffect(() => {
    if (caseStatus?.status === 'PENDING') {
      setLoading(true);
    } else {
      queryClient.invalidateQueries(['cases', caseData.id]);
      setLoading(false);
    }
  }, [caseStatus]);

  React.useEffect(() => {
    if (!caseData.milestones) return;

    const consultFormMilestone = caseData.milestones.find(
      m => m.type === 'consult_form',
    );

    if (consultFormMilestone) {
      setPortfolioConstructionActions(consultFormMilestone.actions);
    }
  }, [caseData.milestones]);

  React.useEffect(() => {
    if (deleteMilestoneActionsError) {
      console.log(deleteMilestoneActionsError?.response?.data?.error);
      setIsDeleteMilestoneErrorModalOpen(true);
    }
  }, [deleteMilestoneActionsError]);

  return (
    <Accordion
      disableGutters
      sx={{
        backgroundColor: '#fff',
        boxShadow: 'none',
        border: 'none',
        '&.MuiAccordion-root::before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          height: '60px',
          borderBottom: 'none',
        }}
      >
        <Box display="flex" alignItems="center" gap="8px" width="100%">
          {/*
           * We don't know when a milestone is complete and when it isn't.
           * When we know, we have to uncomment the next component.
           */}
          {/* <CompleteIconStatus>
            <CheckIcon sx={{ width: '18px', height: '18px' }} />
          </CompleteIconStatus> */}
          <InProgressStatus>{index + 1}</InProgressStatus>
          <MilestoneName milestone={milestone} />
          <MilestoneDate milestone={milestone} />
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <AccordionDetailsDivider milestone={milestone} margin="0 0 8px 36px" />
        {loading ? (
          <LoadingState />
        ) : (
          <>
            {milestone.actions.map((action, index) => (
              <Grid
                container
                key={index}
                m="4px 0 4px 36px"
                width="calc(100% - 36px)"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(200px, 1fr) 120px 80px',
                  alignItems: 'center',
                }}
              >
                <Grid item>
                  <ActionName action={action} />
                </Grid>
                <Grid item sx={{ textAlign: 'center' }}>
                  <ActionDate action={action} />
                </Grid>
                <Grid
                  item
                  sx={{
                    display: 'flex',
                    gap: '0px',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <PortfolioConstructionViewButton
                    milestone={milestone}
                    action={action}
                  />
                  <PerformanceReportViewButton
                    milestone={milestone}
                    action={action}
                  />
                  <PremiumReportViewButton
                    milestone={milestone}
                    action={action}
                  />
                  <ConsultFormViewButton milestone={milestone} />
                  <PooViewButton milestone={milestone} action={action} />
                  <MilestoneActionDeleteButton
                    milestone={milestone}
                    setActionToDelete={setActionToDelete}
                    action={action}
                  />
                </Grid>
              </Grid>
            ))}
            <AccordionDetailsDivider
              milestone={milestone}
              margin="8px 0 0 36px"
            />
            <ConsultFormPromptButton
              caseData={caseData}
              milestone={milestone}
            />
            <PortfolioConstructionPromptButton
              caseData={caseData}
              milestone={milestone}
              portfolioConstructionActions={portfolioConstructionActions}
            />
            <PooPromptButton caseData={caseData} milestone={milestone} />
          </>
        )}
      </AccordionDetails>
      {actionToDelete && (
        <DeleteMilestoneActionModal
          open={actionToDelete !== null}
          handleClose={() => setActionToDelete(null)}
          handleDeleteMilestoneAction={() => {
            deleteMilestoneActionsMutate({
              caseId: actionToDelete.caseId,
              actionId: actionToDelete.id,
              successToastMessage: 'Action deleted successfully',
            });
            setActionToDelete(null);
          }}
          actionName={actionToDelete.versionName}
        />
      )}
      <ErrorDeleteMilestoneModal
        open={isDeleteMilestoneErrorModalOpen}
        handleClose={() => setIsDeleteMilestoneErrorModalOpen(false)}
        milestoneError={deleteMilestoneActionsError?.response?.data}
        milestone={milestone}
      />
    </Accordion>
  );
};

export default AccordionExpandIcon;

const MilestoneName = ({ milestone }: { milestone: CaseMilestone }) => {
  return (
    <Box flex={1}>
      <Typography sx={{ fontSize: '16px', color: '#282829' }}>
        {milestone.displayName}
      </Typography>
    </Box>
  );
};

const MilestoneDate = ({ milestone }: { milestone: CaseMilestone }) => {
  const getMilestoneCreatedAt = (milestone: CaseMilestone) => {
    if (milestone.actions.length === 0) {
      return '';
    } else {
      // sort actions by createdAt
      const sortedActions = milestone.actions.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      return `Created at ${moment(sortedActions[0].createdAt).format('MM/DD/YYYY')}`;
    }
  };
  return (
    <Typography
      sx={{
        paddingRight: '8px',
        fontSize: '12px',
        fontWeight: '400',
        color: '#888',
      }}
    >
      {getMilestoneCreatedAt(milestone)}
    </Typography>
  );
};

const ActionName = ({ action }: { action: CaseMilestoneAction }) => {
  return (
    <Typography
      sx={{
        paddingRight: '8px',
        fontSize: '14px',
        color: '#535256',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        lineHeight: '1.4',
      }}
    >
      {action.versionName}
    </Typography>
  );
};

const ActionDate = ({ action }: { action: CaseMilestoneAction }) => {
  return (
    <Typography sx={{ fontSize: '12px', color: '#aaa' }}>
      {moment(action.createdAt).format('MM/DD/YYYY')}
    </Typography>
  );
};

const ConsultFormViewButton = ({ milestone }: { milestone: CaseMilestone }) => {
  return milestone.type === 'consult_form' ? <ViewButton link="" /> : null;
};

const PortfolioConstructionViewButton = ({
  milestone,
  action,
}: {
  milestone: CaseMilestone;
  action: CaseMilestoneAction;
}) => {
  return milestone.type === 'portfolio_construction' ? (
    <Box display="flex" gap={1}>
      <ViewButton
        link={`/cases/${action.caseId}/portfolio-constructions/${action.id}/summary`}
      />
    </Box>
  ) : null;
};

const PerformanceReportViewButton = ({
  milestone,
  action,
}: {
  milestone: CaseMilestone;
  action: CaseMilestoneAction;
}) => {
  return milestone.type === 'performance_report' ? (
    <Box display="flex" gap={1}>
      <DownloadButton caseId={action.caseId} actionId={action.id} />
    </Box>
  ) : null;
};

const PooViewButton = ({
  milestone,
  action,
}: {
  milestone: CaseMilestone;
  action: CaseMilestoneAction;
}) => {
  return milestone.type === 'presentation_of_options' ? (
    <ViewButton
      link={`/conversations/slides-editor/case/${action.caseId}/action/${action.id}`}
    />
  ) : null;
};

const MilestoneActionDeleteButton = ({
  milestone,
  setActionToDelete,
  action,
}: {
  milestone: CaseMilestone;
  setActionToDelete: React.Dispatch<
    React.SetStateAction<CaseMilestoneAction | null>
  >;
  action: CaseMilestoneAction;
}) => {
  return milestone.type === 'presentation_of_options' ||
    milestone.type === 'premium_report' ||
    milestone.type === 'portfolio_construction' ||
    milestone.type === 'performance_report' ? (
    <IconButton onClick={() => setActionToDelete(action)}>
      <DeleteIcon sx={{ width: '16px', height: '16px', color: '#E11D48' }} />
    </IconButton>
  ) : null;
};

const PremiumReportViewButton = ({
  milestone,
  action,
}: {
  milestone: CaseMilestone;
  action: CaseMilestoneAction;
}) => {
  return milestone.type === 'premium_report' ? (
    <ViewButton
      link={`/conversations/slides-editor/case/${action.caseId}/action/${action.id}`}
    />
  ) : null;
};

const ViewButton = ({ link = '' }: { link?: string }) => {
  return link ? (
    <Box flex={1} textAlign="right">
      <Link
        to={link}
        style={{ display: 'inline-block', textDecoration: 'none' }}
      >
        <Typography sx={{ fontSize: '12px', color: '#0084E3' }}>
          View
        </Typography>
      </Link>
    </Box>
  ) : (
    <Box flex={1} textAlign="right">
      <Typography sx={{ fontSize: '12px', color: '#bbb' }}>View</Typography>
    </Box>
  );
};

const DownloadButton = ({
  caseId,
  actionId,
}: {
  caseId: string;
  actionId: string;
}) => {
  const { isLoading, refetchReportPDF } = useGetDownloadPerformanceReportPDF({
    caseId,
    actions: actionId,
  });

  const handleClick = async () => {
    try {
      const result = await refetchReportPDF();
      const newUrl = result?.data?.data;

      if (newUrl) {
        const link = document.createElement('a');
        link.href = newUrl;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.warn('No URL returned.');
      }
    } catch (err) {
      console.error(err);
      alert('Download failed.');
    }
  };

  return (
    <Box flex={1} textAlign="right">
      <Typography
        sx={{
          fontSize: '12px',
          color: isLoading ? '#bbb' : '#0084E3',
          cursor: isLoading ? 'default' : 'pointer',
        }}
        onClick={!isLoading ? handleClick : undefined}
      >
        {isLoading ? <CircularProgress size={14} /> : 'Download'}
      </Typography>
    </Box>
  );
};

const AccordionDetailsDivider = ({
  milestone,
  margin,
}: {
  milestone: CaseMilestone;
  margin: string;
}) => {
  return milestone.actions.length > 0 ? <Divider sx={{ margin }} /> : null;
};

const ConsultFormPromptButton = ({
  caseData,
  milestone,
}: {
  caseData: Case;
  milestone: CaseMilestone;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleQuestionClick = () => {
    dispatch(
      chatActions.setSearchQueryData({
        query: `Create a new consult form for ${caseData.name}`,
        data: { caseId: caseData.id },
      }),
    );
    navigate('/conversations/start');
  };

  return milestone.type === 'consult_form' ? (
    <Box sx={{ marginLeft: '36px' }}>
      <PrompQuestions
        showDividers
        itemPy="12px"
        questions={['Create New Form']}
        handleQuestionClick={handleQuestionClick}
      />
    </Box>
  ) : null;
};

const PooPromptButton = ({
  caseData,
  milestone,
}: {
  caseData: Case;
  milestone: CaseMilestone;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleQuestionClick = (question: string) => {
    dispatch(
      chatActions.setSearchQueryData({
        query: `${question} for ${caseData!.name}`,
        data: { caseId: caseData!.id },
      }),
    );
    navigate('/conversations/start');
  };

  return milestone.type === 'presentation_of_options' ? (
    <Box sx={{ marginLeft: '36px' }}>
      <PrompQuestions
        showDividers
        itemPy="12px"
        questions={['Generate presentation of options']}
        handleQuestionClick={() => {
          handleQuestionClick('Generate presentation of options');
        }}
      />
    </Box>
  ) : milestone.type === 'premium_report' ? (
    <Box sx={{ marginLeft: '36px' }}>
      <PrompQuestions
        showDividers
        itemPy="12px"
        questions={['Generate premium report']}
        handleQuestionClick={() => {
          handleQuestionClick('Generate premium report');
        }}
      />
    </Box>
  ) : null;
};

const PortfolioConstructionPromptButton = ({
  caseData,
  milestone,
  portfolioConstructionActions,
}: {
  caseData: Case;
  milestone: CaseMilestone;
  portfolioConstructionActions: CaseMilestoneAction[];
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { mutateAsync, isLoading } =
    useCreatePortfolioConstructionByConsultForm();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action: CaseMilestoneAction) => {
    setAnchorEl(null);
    mutateAsync({
      caseId: caseData.id,
      milestoneActionId: action.id,
      versionName: action.versionName,
      successToastMessage: 'Portfolio construction created successfully',
    });
  };

  return milestone.type === 'portfolio_construction' ? (
    <Box sx={{ margin: '16px 0 0 36px' }}>
      <LoadingButton
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        endIcon={
          !isLoading ? (
            <ArrowBackIosNewIcon
              sx={{
                transform: anchorEl ? 'rotate(90deg)' : 'rotate(270deg)',
                transition: 'transform 0.3s ease-in-out',
                fontSize: '8px',
                width: '12px',
                height: '12px',
              }}
            />
          ) : (
            <CircularProgress size={14} />
          )
        }
        sx={{
          backgroundColor: isLoading ? '#E0E0E0' : '#F1F1F1',
          color: isLoading ? '#666666' : '#000000',
          textTransform: 'none',
          borderRadius: '20px',
          fontFamily: 'Inter',
          fontWeight: '500',
          fontSize: '12px',
          lineHeight: '16px',
          letterSpacing: '0%',
          textAlign: 'center',
          padding: '8px 16px',
          minWidth: '140px',
          height: '32px',
          '&:hover': {
            backgroundColor: isLoading ? '#E0E0E0' : '#F1F1F1',
          },
          '&.Mui-disabled': {
            backgroundColor: '#E0E0E0',
            color: '#666666',
          },
        }}
        disabled={isLoading}
      >
        {!isLoading ? 'Create new portfolio' : 'Creating portfolio...'}
      </LoadingButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        {portfolioConstructionActions.map(action => (
          <MenuItem
            key={action.id}
            value={action.id}
            onClick={() => handleMenuItemClick(action)}
          >
            {`Based on ${action.versionName}`}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  ) : null;
};

// const CompleteIconStatus = styled.div`
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   width: 28px;
//   height: 28px;
//   background-color: #1bc29a;
//   border-radius: 50%;
//   color: #fff;
// `;

const InProgressStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background-color: #f5f5f5;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 500;
  color: #282829;
`;

// Loading animation component
const LoadingState = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        marginLeft: '36px',
      }}
    >
      <CircularProgress
        size={24}
        sx={{ marginBottom: '8px', color: '#0084E3' }}
      />
      <Typography variant="body2" color="text.secondary">
        Optimizing the model sets... This may take a few moments
      </Typography>
    </Box>
  );
};
